<template>
  <v-container fluid class="pa-4">
    <v-progress-linear v-if="loading" indeterminate color="primary" />
    
    <v-card class="mx-auto" max-width="100%">
      <v-card-title class="d-flex justify-start align-center bg-grey-lighten-4">
        <!-- ✅ Portfolio Info Header -->
        <div v-if="portfolioInfo" class="ml-4">
          <span class="font-weight-bold text-h6">{{ portfolioInfo.portfolioName }}</span>
          <span class="ml-2 text-caption">
            ({{ formatDate(portfolioInfo.portfolioStart) }} – {{ portfolioInfo.portfolioEnd ? formatDate(portfolioInfo.portfolioEnd) : 'Active' }})
          </span>
          <span class="ml-4 text-h6" :class="getColorClass(portfolioInfo.pnl)">
            ₹{{ Number(portfolioInfo.pnl).toFixed(0) }}
          </span>
        </div>

        <v-btn color="secondary" icon variant="text" size="large" title="Refresh PnL" class="ml-1" @click="refreshPnL">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>

        <v-btn variant="text" color="primary" @click="showMarginDialog = true">Margin</v-btn>

        <!-- 📌 Close Portfolio Button -->
        <v-menu v-model="showCloseMenu" :close-on-content-click="false" offset-y>
          <template #activator="{ props }">
            <v-btn variant="text" color="red" v-bind="props">Close Portfolio</v-btn>
          </template>
          <v-card class="pa-3" max-width="300">
            <v-date-picker v-model="closeDate" />
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="showCloseMenu = false">Cancel</v-btn>
              <v-btn color="red" text @click="closePortfolio">Confirm</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <v-spacer></v-spacer>
        
      <NiftyLevel v-slot="{ currentLevel } = {}">
        <span v-if="portfolioInfo?.niftyLevelAtStart && currentLevel" class="ml-2">
          &nbsp;&nbsp;&nbsp;&nbsp;|| &nbsp;Start Level: {{ portfolioInfo.niftyLevelAtStart }}
          <span :class="Number(currentLevel - portfolioInfo.niftyLevelAtStart) < 0 ? 'text-red' : 'text-green'">
            [{{ (currentLevel - portfolioInfo.niftyLevelAtStart).toFixed(0) }}]
          </span>
        </span>
      </NiftyLevel> 
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- 🟢 Open Positions Section -->
        <div class="pa-4">
          <h3 class="text-success mb-2"><v-icon color="success">mdi-circle-medium</v-icon> Open Positions</h3>
          <v-data-table 
            :headers="headers" 
            :items="openPositions"
            :items-per-page="-1" 
            hide-default-footer 
            class="elevation-1 rounded"
          >
            <template #item="{ item }">
              <tr :class="getRowClass(item)">
                <td>
                  <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'" 
                    :max-width="item.application === 'BREEZE' ? 40 : 20" cover />
                </td>
                <td>{{ formatDate(item.expiry_date) }}</td>
                <td>{{ item.strike_price }}</td>
                <td>{{ item.right }}</td>
                <td>{{ item.action }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.average_price }}</td>
                <td>{{ item.ltp }}</td>
                <td><v-chip size="x-small" color="success">{{ item.positionStatus }}</v-chip></td>
                <td>
                  <div class="d-flex align-center">
                    <v-text-field v-model="item.exit_price" variant="outlined" density="compact" hide-details class="exit-input" />
                    <v-icon color="primary" size="20" class="ml-1" @click="updatePositionFromOrder(item)">mdi-refresh</v-icon>
                  </div>
                </td>
                <td><span :class="getColorClass(item.position_pnl)">{{ item.position_pnl }}</span></td>
                <td>
                  <v-icon color="orange" size="24" :disabled="isExitDisabled(item)" @click="confirmExit(item)">mdi-exit-to-app</v-icon>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>

        <!-- 🔴 Closed Positions Section -->
        <v-expansion-panels v-model="expandedPanels" flat>
          <v-expansion-panel class="border-t">
            <v-expansion-panel-title class="bg-grey-lighten-5">
              <strong>Closed Positions ({{ closedPositions?.length || 0 }})</strong>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-0">
              <v-data-table 
                :headers="headers" 
                :items="closedPositions"
                :items-per-page="-1" 
                hide-default-footer
              >
                <template #item="{ item }">
                  <tr class="closed-row">
                    <td><v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'" :max-width="20" cover /></td>
                    <td>{{ formatDate(item.expiry_date) }}</td>
                    <td>{{ item.strike_price }}</td>
                    <td>{{ item.right }}</td>
                    <td>{{ item.action }}</td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ item.average_price }}</td>
                    <td>{{ item.ltp }}</td>
                    <td>
                      <v-select v-if="item.editing" v-model="item.positionStatus" :items="['OPEN', 'CLOSED']" density="compact" hide-details />
                      <span v-else>{{ item.positionStatus }}</span>
                    </td>
                    <td>
                      <v-text-field v-model="item.exit_price" density="compact" hide-details :disabled="!item.editing" />
                    </td>
                    <td><span :class="getColorClass(item.position_pnl)">{{ item.position_pnl }}</span></td>
                    <td class="text-center">
                      <v-icon v-if="!item.editing" color="primary" size="20" @click="enableEditing(item)">mdi-pencil</v-icon>
                      <template v-else>
                        <v-icon color="green" @click="savePosition(item)">mdi-check</v-icon>
                        <v-icon color="red" @click="cancelEditing(item)">mdi-close</v-icon>
                      </template>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <!-- 📌 Lifecycle Management Section -->
          <div class="pa-4 mt-4 border-t">
            <div class="d-flex justify-between align-center mb-4">
              <h3 class="text-primary">Next Stage: {{ nextStageLabel }}</h3>
              <h3 class="text-h6">TOTAL P&L: 
                <span :class="getColorClass(portfolioInfo?.pnl)">₹{{ Number(portfolioInfo?.pnl || 0).toFixed(0) }}</span>
              </h3>
            </div>

            <!-- Stage Selection Buttons -->
            <div class="d-flex flex-wrap mb-4">
              <v-btn
                v-for="stage in stages"
                :key="stage.id"
                :color="selectedStage === stage.id ? 'success' : 'grey-lighten-2'"
                class="mr-2 mb-2"
                @click="fetchStageSetup(stage.id)"
              >
                {{ stage.name }}
                <v-icon v-if="recommendedStage === stage.id" right size="small" class="ml-1">mdi-check-circle</v-icon>
              </v-btn>
            </div>

            <!-- Execution Zone -->
            <v-row class="execution-zone rounded pa-4 mx-0 mb-4" align="center">
              <v-col cols="12" md="8">
                <div v-for="(line, index) in executionLines" :key="index" class="text-h6 mb-2">
                  <v-chip size="small" label color="grey-darken-3" class="mr-3">{{ line.instrument }}</v-chip>
                  <v-icon size="small" class="mr-3">mdi-arrow-right</v-icon>
                  <span :class="line.action === 'Buy' ? 'text-blue' : 'text-red'">{{ line.action.toUpperCase() }}</span>
                </div>
                <div v-if="executionLines.length === 0" class="text-muted">Select a stage to see trade setup</div>
              </v-col>
              
              <v-col cols="12" md="4" class="text-center border-left">
                <div class="cursor-pointer" @click="handleExitAll">
                  <v-icon color="red" size="48">mdi-account-exit</v-icon>
                  <div class="text-red font-weight-bold">Exit All</div>
                </div>
              </v-col>
            </v-row>

            <!-- Bottom Actions -->
            <div class="d-flex justify-space-between align-center">
              <v-btn variant="text" color="grey" @click="resetSetup">
                <v-icon class="mr-1">mdi-undo</v-icon> Reset/Undo
              </v-btn>
              <v-btn color="success" size="large" class="px-10 font-weight-bold" @click="executeStage">
                EXECUTE
              </v-btn>
              <div style="width: 100px"></div>
            </div>
          </div>
      </v-card-text>
    </v-card>

    <!-- 📌 Margin Dialog (Unchanged) -->
    <v-dialog v-model="showMarginDialog" max-width="800">
      <v-card>
        <v-card-title>Margin Details</v-card-title>
        <v-card-text>
          <v-progress-linear v-if="loadingMargin" indeterminate />
          <v-data-table v-else :headers="marginHeaders" :items="marginData" class="elevation-1" hide-default-footer />
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="showMarginDialog = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import NiftyLevel from '@/components/trades/NiftyLevel.vue'
import { useStore } from 'vuex'
import { useDialog } from '@/composables/useDialog'
import { useRoute } from 'vue-router'

const route = useRoute()
const store = useStore()
const { alert, confirm } = useDialog()
// Add these with your other refs
const expandedPanels = ref([0]); // [0] ensures the first panel starts open

// Add the refresh function
const refreshPnL = async () => {
  try {
    loading.value = true;
    // Call your refresh actions here
    await store.dispatch('positions/refreshLtpForPositions');
    await getPortfolioTrade(); // Reload data after refresh
  } catch (error) {
    console.error('Failed to refresh:', error);
  } finally {
    loading.value = false;
  }
};

// State
const loading = ref(true)
const loadingMargin = ref(false)
const showMarginDialog = ref(false)
const allTrades = ref([])
const portfolioInfo = ref(null)
const showCloseMenu = ref(false)
const closeDate = ref(new Date().toISOString().substr(0, 10))

// Testing ID provided
const testPortfolioId = '69ba6c5886552026647b51eb'
const currentPortfolioId = ref(route.query.portfolioId || route.params.portfolioId || testPortfolioId)

const headers = [
  { title: 'App', key: 'application' },
  { title: 'Expiry', key: 'expiry_date' },
  { title: 'Strike', key: 'strike_price' },
  { title: 'Right', key: 'right' },
  { title: 'Type', key: 'action' },
  { title: 'Qty', key: 'quantity' },
  { title: 'Buy Price', key: 'average_price' },
  { title: 'LTP', key: 'ltp' },
  { title: 'Status', key: 'positionStatus' },
  { title: 'Exit Price', key: 'exit_price' },
  { title: 'PNL', key: 'position_pnl' },
  { title: 'Action', key: 'Action', sortable: false }
]
// --- Lifecycle State ---
const recommendedStage = ref('');
const selectedStage = ref('');
const executionLines = ref([]);
const stages = [
  { id: 'WEEKLY_ROLLOVER', name: 'Weekly R/O' },
  { id: 'MONTHLY_ROLLOVER', name: 'Monthly R/O' },
  { id: 'UPPER_THRESHOLD', name: 'Upper Threshold' },
  { id: 'LOWER_THRESHOLD', name: 'Lower Threshold' }
];

const nextStageLabel = computed(() => {
  const stage = stages.find(s => s.id === selectedStage.value);
  return stage ? stage.name : 'Checking...';
});

// --- API Methods ---
const fetchNextStage = async () => {
  try {
    // API: getNextStage(GET)
    const res = await store.dispatch('coveredcall/getNextStage');
    recommendedStage.value = res; // e.g., 'WEEKLY_ROLLOVER'
    selectedStage.value = res;
    await fetchStageSetup(res);
  } catch (err) {
    console.error('Failed to fetch next stage:', err);
  }
};

const fetchStageSetup = async (stageId) => {
  selectedStage.value = stageId;
  try {
    let setupData;
    const pId = currentPortfolioId.value;

    // Call respective GET setup API based on button selection
    if (stageId === 'WEEKLY_ROLLOVER') setupData = await store.dispatch('coveredcall/getWeeklyRolloverSetup', pId);
    if (stageId === 'MONTHLY_ROLLOVER') setupData = await store.dispatch('coveredcall/getMonthlyRolloverSetup', pId);
    if (stageId === 'UPPER_THRESHOLD') setupData = await store.dispatch('coveredcall/getUpperThresholdSetup', pId);
    if (stageId === 'LOWER_THRESHOLD') setupData = await store.dispatch('coveredcall/getLowerThresholdSetup', pId);

    // Mock/Mapping logic for executionLines based on API output
    // This should be mapped from your "initSetup" or "rolloverSetup" outputs
    executionLines.value = [
      { instrument: 'CEw', action: 'Buy' },
      { instrument: 'CEw', action: 'Sell' },
      { instrument: 'CEm', action: 'Buy' },
      { instrument: 'CEm', action: 'Sell' }
    ];
  } catch (err) {
    console.error('Failed to fetch stage setup:', err);
  }
};

const executeStage = async () => {
  try {
    loading.value = true;
    // API: executeNextStage(POST)
    await store.dispatch('coveredcall/executeNextStage', {
      coverredCallNextStage: selectedStage.value
    });
    store.commit('snackbar/showMessage', { text: 'Stage Executed Successfully', color: 'success' });
    await getPortfolioTrade(); // Refresh tables
  } catch (err) {
    store.commit('snackbar/showMessage', { text: 'Execution Failed', color: 'error' });
  } finally {
    loading.value = false;
  }
};

const handleExitAll = () => {
  confirm({
    title: 'Exit All Positions',
    message: 'This will close all open trades. Proceed?',
    onConfirm: async () => {
      await store.dispatch('coveredcall/exitBullPutSpread');
      await getPortfolioTrade();
    }
  });
};

const resetSetup = () => {
  fetchNextStage();
};


// Computed
const openPositions = computed(() => {
  return allTrades.value?.flatMap(t => t.positions || []).filter(p => p.positionStatus === 'OPEN') || []
})

const closedPositions = computed(() => {
  return allTrades.value?.flatMap(t => t.positions || []).filter(p => p.positionStatus === 'CLOSED') || []
})

// ✅ Missing helper functions restored
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const day = date.getDate().toString().padStart(2, '0')
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear().toString().slice(-2)
  return `${day}-${month}-${year}`
}

const getColorClass = (value) => {
  return Number(value) < 0 ? 'text-red' : 'text-green'
}

const getRowClass = (item) => {
  return item.positionStatus === 'CLOSED' ? 'closed-row' : ''
}

// Actions
const getPortfolioTrade = async () => {
  try {
    loading.value = true;
    const res = await store.dispatch('portfolio/getPortfolioTrade', { 
      portfolioId: currentPortfolioId.value 
    });

    console.log('Raw API Response:', res);

    // 1. Precise spelling match for the provided JSON
    const detail = res?.portoflioTradeDatail; 

    if (detail) {
      portfolioInfo.value = detail.porfolioInfo;
      console.log('Portfolio Info Mapped:', portfolioInfo.value);
      
      // 2. Map the trade list and flatten the positions
      allTrades.value = (detail.tradeDetailList || []).map(trade => {
        return {
          ...trade,
          // Mapping positionInfoList (from API) to positions (for Computed properties)
          positions: (trade.positionInfoList || []).map(pos => ({
            ...pos,
            // Force status to UPPERCASE for the .filter() logic
            positionStatus: String(pos.positionStatus || '').toUpperCase(),
            
            // Ensure numbers are handled
            position_pnl: pos.position_pnl || 0,
            exit_price: pos.exit_price || null
          }))
        };
      });
      
      console.log('Processed trades with positions:', allTrades.value);
      console.log('Open positions found:', openPositions.value.length);
      console.log('Closed positions found:', closedPositions.value.length);
    } else {
      console.error('Property "portoflioTradeDatail" not found in response');
    }
  } catch (e) {
    console.error('Data mapping failed:', e);
  } finally {
    loading.value = false;
  }
};


// --- Update onMounted ---
onMounted(async () => {
  await getPortfolioTrade();
  await fetchNextStage(); // Get stage recommendation after trade data
});

const isExitDisabled = (item) => {
  return !item.exit_price || isNaN(item.exit_price);
};

const confirmExit = (item) => {
  confirm({
    title: 'Confirm Exit',
    message: 'Are you sure you want to close this position?',
    onConfirm: () => savePosition(item)
  });
};

const enableEditing = (item) => {
  item.editing = true;
};

const cancelEditing = (item) => {
  item.editing = false;
};

const updatePositionFromOrder = async (item) => {
  // Add your logic to fetch exit price from orders
  console.log('Updating from order for:', item.optionPositionId);
};
const savePosition = async (item) => {
  // Validate that CLOSED positions have an exit price
  if (item.positionStatus === 'CLOSED' && (item.exit_price == null || item.exit_price === '')) {
    store.commit('snackbar/showMessage', {
      text: 'Please enter exit price before saving',
      color: 'error'
    });
    return;
  }

  try {
    loading.value = true;
    const payload = {
      optionPositionId: item.optionPositionId,
      status: item.positionStatus,
      exitPrice: item.exit_price === null || item.exit_price === '' ? 0 : Number(item.exit_price)
    };
    
    // Call the API via Vuex action
    await store.dispatch('positions/updateExitPrice', payload);

    // Refresh the local data after a successful save
    await getPortfolioTrade();

    store.commit('snackbar/showMessage', {
      text: 'Position updated successfully',
      color: 'success'
    });
  } catch (err) {
    console.error('Failed to update position:', err);
    store.commit('snackbar/showMessage', {
      text: 'Failed to update position',
      color: 'error'
    });
  } finally {
    loading.value = false;
    item.editing = false;
  }
};
</script>

<style scoped>
/* ... (Keep existing styles) ... */
.bg-grey-lighten-4 { background-color: #f5f5f5 !important; }
.bg-grey-lighten-5 { background-color: #fafafa !important; }
.border-t { border-top: 1px solid #e0e0e0; }
.execution-zone {
  background-color: #fffde7 !important; /* Soft yellow matching image_0a0eaf.png */
  border: 1px solid #ffe57f;
}

.border-left {
  border-left: 1px solid #e0e0e0;
}

.cursor-pointer {
  cursor: pointer;
}

.text-blue {
  color: #1e88e5;
}

.text-red {
  color: #e53935;
}
</style>